import React, {
  useMemo,
} from "react";
import PropTypes from "prop-types";

import {
  assertStudioRuntime,
  createStudioRuntime,
} from "../../engines/StudioPluginEngine";
import { isStudioDefinition } from "./StudioDefinition";
import {
  StudioRuntimeProvider,
} from "./StudioRuntimeContext";
import {
  StudioThemeProvider,
} from "./StudioThemeContext";
import {
  DockingHost,
  DockingProvider,
} from "./Docking";
import StudioRuntimeComposition from "./Runtime/StudioRuntimeComposition";

const EMPTY_PLUGINS = Object.freeze([]);

/**
 * GenericStudioHost
 *
 * Generic UX entry point for every Studio domain.
 *
 * The host owns or receives one isolated StudioRuntime, installs its
 * React provider and delegates capability materialization to
 * StudioRuntimeComposition.
 */
export default function GenericStudioHost({
  mode = "standalone",
  definition = null,
  runtime = null,
  plugins = EMPTY_PLUGINS,
  themeRuntime = null,
  dockingRuntime = null,
  dockPanelRegistry = null,
  header = null,
  explorer = null,
  canvas = null,
  inspector = null,
  assets = null,
  statusBar = null,
}) {
  const resolvedRuntime = useMemo(
    () => {
      if (!Array.isArray(plugins)) {
        throw new TypeError(
          "GenericStudioHost.plugins must be an array."
        );
      }

      if (runtime !== null) {
        try {
          return assertStudioRuntime(runtime);
        } catch (error) {
          throw new TypeError(
            "GenericStudioHost.runtime must be a valid StudioRuntime."
          );
        }
      }

      return createStudioRuntime({ plugins });
    },
    [plugins, runtime]
  );

  if (definition !== null && !isStudioDefinition(definition)) {
    throw new TypeError(
      "GenericStudioHost.definition must be a valid StudioDefinition."
    );
  }

  const slots = definition || {
    header,
    explorer,
    canvas,
    inspector,
    assets,
    statusBar,
  };

  if (
    (dockingRuntime === null) !== (dockPanelRegistry === null)
  ) {
    throw new TypeError(
      "GenericStudioHost requires dockingRuntime and "
        + "dockPanelRegistry together."
    );
  }

  const composition = dockingRuntime === null ? (
    <StudioRuntimeComposition
      mode={mode}
      slots={slots}
    />
  ) : (
    <DockingProvider runtime={dockingRuntime}>
      <DockingHost registry={dockPanelRegistry} />
    </DockingProvider>
  );

  return (
    <StudioRuntimeProvider runtime={resolvedRuntime}>
      {themeRuntime === null ? (
        composition
      ) : (
        <StudioThemeProvider themeRuntime={themeRuntime}>
          {composition}
        </StudioThemeProvider>
      )}
    </StudioRuntimeProvider>
  );
}

GenericStudioHost.propTypes = {
  mode: PropTypes.oneOf([
    "standalone",
    "embedded",
    "fullscreen",
  ]),
  runtime: PropTypes.shape({
    registerPlugin: PropTypes.func.isRequired,
    unregisterPlugin: PropTypes.func.isRequired,
    resolveCapabilities: PropTypes.func.isRequired,
    resolveContributions: PropTypes.func.isRequired,
  }),
  plugins: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      version: PropTypes.string.isRequired,
      capabilities: PropTypes.array.isRequired,
      requires: PropTypes.array.isRequired,
      metadata: PropTypes.object.isRequired,
    })
  ),
  themeRuntime: PropTypes.shape({
    setTheme: PropTypes.func.isRequired,
    getTheme: PropTypes.func.isRequired,
    getThemeId: PropTypes.func.isRequired,
    clear: PropTypes.func.isRequired,
    subscribe: PropTypes.func.isRequired,
  }),
  dockingRuntime: PropTypes.shape({
    getLayout: PropTypes.func.isRequired,
    setLayout: PropTypes.func.isRequired,
    activatePanel: PropTypes.func.isRequired,
    movePanel: PropTypes.func.isRequired,
    resizeSplit: PropTypes.func.isRequired,
    closePanel: PropTypes.func.isRequired,
    hidePanel: PropTypes.func.isRequired,
    restorePanel: PropTypes.func.isRequired,
    resetLayout: PropTypes.func.isRequired,
    subscribe: PropTypes.func.isRequired,
  }),
  dockPanelRegistry: PropTypes.shape({
    get: PropTypes.func.isRequired,
    has: PropTypes.func.isRequired,
    list: PropTypes.func.isRequired,
  }),
  definition: PropTypes.shape({
    id: PropTypes.string.isRequired,
    header: PropTypes.node,
    explorer: PropTypes.node,
    canvas: PropTypes.node,
    inspector: PropTypes.node,
    assets: PropTypes.node,
    statusBar: PropTypes.node,
  }),
  header: PropTypes.node,
  explorer: PropTypes.node,
  canvas: PropTypes.node,
  inspector: PropTypes.node,
  assets: PropTypes.node,
  statusBar: PropTypes.node,
};
